import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { HiExclamationTriangle, HiInformationCircle } from 'react-icons/hi2';
import {
  Backdrop,
  Dialog,
  IconCircle,
  Title,
  Message,
  Actions,
  CancelButton,
  ConfirmButton,
} from './ConfirmDialog.styled';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  variant?: 'danger' | 'warning' | 'info';
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const variantIcons = {
  danger: HiExclamationTriangle,
  warning: HiExclamationTriangle,
  info: HiInformationCircle,
};

export default function ConfirmDialog({
  open,
  title,
  message,
  variant = 'danger',
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    },
    [onCancel],
  );

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => dialogRef.current?.focus());
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  const Icon = variantIcons[variant];

  return createPortal(
    <>
      <Backdrop onClick={onCancel} />
      <Dialog
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-message"
        tabIndex={-1}
      >
        <IconCircle $variant={variant}>
          <Icon />
        </IconCircle>
        <Title id="confirm-title">{title}</Title>
        <Message id="confirm-message">{message}</Message>
        <Actions>
          <CancelButton onClick={onCancel} type="button">
            {cancelLabel ?? t('confirm.cancel')}
          </CancelButton>
          <ConfirmButton $variant={variant} onClick={onConfirm} type="button">
            {confirmLabel ?? t('confirm.confirm')}
          </ConfirmButton>
        </Actions>
      </Dialog>
    </>,
    document.body,
  );
}
